import * as echarts from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent } from 'echarts/components'
import { LegacyGridContainLabel } from 'echarts/features'
import { CanvasRenderer } from 'echarts/renderers'
import { useEffect, useRef, useState } from 'react'
import type { EChartsCoreOption } from 'echarts/core'

/**
 * Only the pieces the dashboard actually draws are registered. ECharts ships every chart
 * type it knows about; importing the whole thing would put several hundred kilobytes of
 * charts nobody looks at into a bundle served from a €7.60 box.
 *
 * The legend and title components are deliberately absent: both are rendered as HTML so
 * they are selectable, translatable and reachable by a screen reader, which canvas text
 * is not.
 */
// LegacyGridContainLabel is not optional here. ECharts 6 removed `grid.containLabel` from the
// default build and ignores it with only a console note, so without this the axis labels are
// left out of the layout calculation and the plot silently overlaps them.
echarts.use([
  LineChart,
  BarChart,
  GridComponent,
  TooltipComponent,
  LegacyGridContainLabel,
  CanvasRenderer,
])

export { echarts }

/**
 * Binds an ECharts instance to a div for the lifetime of the component.
 *
 * `notMerge` is on for every update: merging is how a chart ends up showing a series that
 * the current data no longer contains, which on a portfolio screen means a holding that
 * has been sold still drawn on the page.
 */
export function useECharts(option: EChartsCoreOption | null) {
  // A callback ref, not `useRef`, because the div these charts live in is conditionally
  // rendered - a card shows an empty state until its data arrives, and a table view swaps
  // the canvas out entirely. With a plain ref the init effect runs once on mount, finds
  // nothing there, and never runs again: the chart is silently absent for the rest of the
  // session. Tracking the node as state re-runs init the moment it actually attaches.
  const [node, setNode] = useState<HTMLDivElement | null>(null)
  const chart = useRef<echarts.ECharts | null>(null)

  useEffect(() => {
    if (!node) {
      return
    }
    const instance = echarts.init(node, null, { renderer: 'canvas' })
    chart.current = instance

    // ECharts cannot see a flex or grid resize on its own.
    const observer = new ResizeObserver(() => instance.resize())
    observer.observe(node)

    return () => {
      observer.disconnect()
      instance.dispose()
      chart.current = null
    }
  }, [node])

  useEffect(() => {
    if (chart.current && option) {
      chart.current.setOption(option, { notMerge: true })
    }
  }, [option, node])

  return setNode
}
